import React, { useState, useEffect } from 'react';
import { 
  QrCode, Plus, Trash2, Download, Users, Eye, Copy, 
  ExternalLink, Check, AlertCircle, Settings, BarChart3,
  Phone, MessageSquare, Share2
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../utils/api';
import { toast } from 'sonner';

const QRLeadCollector = () => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    welcome_message: 'नमस्ते! मैं आपसे जुड़ना चाहता/चाहती हूँ।',
    owner_whatsapp: '',
    button_text: 'WhatsApp पर जुड़ें',
    thank_you_message: 'धन्यवाद! आपका नंबर सेव हो गया है।'
  });

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchSettings();
    fetchCampaigns();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/qr-leads/admin/settings');
      setSettings(response.data);
    } catch (error) {
      console.log('Settings not available');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/qr-leads/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setSettings({ is_enabled: false });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async (campaignId) => {
    try {
      const response = await api.get(`/qr-leads/campaigns/${campaignId}/leads`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/qr-leads/campaigns', form);
      toast.success('Campaign created!');
      setShowCreateModal(false);
      setForm({
        name: '',
        welcome_message: 'नमस्ते! मैं आपसे जुड़ना चाहता/चाहती हूँ।',
        owner_whatsapp: '',
        button_text: 'WhatsApp पर जुड़ें',
        thank_you_message: 'धन्यवाद! आपका नंबर सेव हो गया है।'
      });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all its leads?')) return;
    try {
      await api.delete(`/qr-leads/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign(null);
        setLeads([]);
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleExportCSV = async (campaign) => {
    try {
      const response = await api.get(`/qr-leads/campaigns/${campaign.id}/export`);
      const { csv_content, filename } = response.data;
      
      const blob = new Blob([csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = document.getElementById('campaign-qr');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const a = document.createElement('a');
      a.download = `${selectedCampaign?.name || 'qr'}_code.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!settings?.is_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">QR Lead Collector</h1>
          <p className="text-muted-foreground mb-4">
            This tool is currently disabled by admin. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4" data-testid="qr-lead-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">QR Lead Collector</h1>
            <p className="text-muted-foreground">WhatsApp numbers collect करें QR scan से</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-5 h-5" />
            New Campaign
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaigns List */}
          <div className="lg:col-span-1">
            <div className="glass-card p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-green-500" />
                Campaigns ({campaigns.length})
              </h3>
              
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {campaigns.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No campaigns yet. Create your first QR campaign!
                  </p>
                ) : (
                  campaigns.map(campaign => (
                    <div
                      key={campaign.id}
                      onClick={() => { setSelectedCampaign(campaign); fetchLeads(campaign.id); }}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedCampaign?.id === campaign.id
                          ? 'bg-green-500/20 border border-green-500/50'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            ID: {campaign.id}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          campaign.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                        }`}>
                          {campaign.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {campaign.lead_count || 0} leads
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {campaign.total_scans || 0} scans
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Campaign Details & Leads */}
          <div className="lg:col-span-2">
            {selectedCampaign ? (
              <div className="space-y-4">
                {/* Campaign Info */}
                <div className="glass-card p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold">{selectedCampaign.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        WhatsApp: {selectedCampaign.owner_whatsapp}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowQRModal(true)}
                        className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                      >
                        <QrCode className="w-4 h-4" />
                        View QR
                      </button>
                      <button
                        onClick={() => handleExportCSV(selectedCampaign)}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        Export
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(selectedCampaign.id)}
                        className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-white/5 rounded-xl text-center">
                      <p className="text-2xl font-bold text-green-500">{selectedCampaign.lead_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Leads</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-xl text-center">
                      <p className="text-2xl font-bold text-blue-500">{selectedCampaign.total_scans || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Scans</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-xl text-center">
                      <p className="text-2xl font-bold text-yellow-500">
                        {selectedCampaign.total_scans > 0 
                          ? Math.round((selectedCampaign.lead_count / selectedCampaign.total_scans) * 100)
                          : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">Conversion</p>
                    </div>
                  </div>
                  
                  {/* Landing Page Link */}
                  <div className="mt-4 p-3 bg-green-500/10 rounded-xl">
                    <p className="text-xs text-muted-foreground mb-2">Landing Page URL:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${baseUrl}/api/qr-leads/page/${selectedCampaign.id}`}
                        className="flex-1 px-3 py-2 bg-white/5 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(`${baseUrl}/api/qr-leads/page/${selectedCampaign.id}`)}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <a
                        href={`${baseUrl}/api/qr-leads/page/${selectedCampaign.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Leads List */}
                <div className="glass-card p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-green-500" />
                    Collected Leads ({leads.length})
                  </h3>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {leads.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No leads yet. Share your QR code!
                      </p>
                    ) : (
                      leads.map(lead => (
                        <div key={lead.id} className="p-3 bg-white/5 rounded-xl flex items-center justify-between">
                          <div>
                            <p className="font-medium">{lead.name || 'No Name'}</p>
                            <p className="text-sm text-green-500">{lead.whatsapp_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(lead.created_at).toLocaleString('en-IN')}
                            </p>
                          </div>
                          <a
                            href={`https://wa.me/${lead.whatsapp_number}`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                          >
                            <MessageSquare className="w-5 h-5" />
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="glass-card p-8 text-center">
                <QrCode className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Select a campaign to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">Create New Campaign</h3>
              <form onSubmit={handleCreateCampaign} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Campaign Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="My Shop QR"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your WhatsApp Number *</label>
                  <input
                    type="tel"
                    value={form.owner_whatsapp}
                    onChange={(e) => setForm({ ...form, owner_whatsapp: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="919876543210"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">With country code, no + or spaces</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Welcome Message</label>
                  <textarea
                    value={form.welcome_message}
                    onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Message shown on landing page</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Button Text</label>
                  <input
                    type="text"
                    value={form.button_text}
                    onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Thank You Message</label>
                  <input
                    type="text"
                    value={form.thank_you_message}
                    onChange={(e) => setForm({ ...form, thank_you_message: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-2 bg-white/10 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-green-500 text-white rounded-xl font-medium"
                  >
                    Create Campaign
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {showQRModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-sm w-full text-center">
              <h3 className="text-xl font-bold mb-4">{selectedCampaign.name}</h3>
              
              <div className="bg-white p-4 rounded-2xl inline-block mb-4">
                <QRCodeSVG
                  id="campaign-qr"
                  value={`${baseUrl}/api/qr-leads/page/${selectedCampaign.id}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR to collect WhatsApp leads
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={downloadQR}
                  className="flex-1 py-2 bg-green-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download QR
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="flex-1 py-2 bg-white/10 rounded-xl font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRLeadCollector;
